<template>
  <browse-tabs browse :model-value="BROWSE_TABS.TOKEN_PACKS" />

  <div class="tw-w-full tw-flex tw-flex-col tw-items-center">
    <div class="tw-w-full tw-max-w-screen-lg">
      <p class="tw-text-h3 tw-font-bold tw-leading-none tw-text-center tw-mb-3">
        {{ $t('marketplace.vc_prompt') }}
      </p>

      <p class="tw-text-center tw-mb-6">
        {{ $t('marketplace.vc_description', { game: game.name }) }}
      </p>

      <div class="tw-space-y-3 tw-mb-6" :class="Classes.VIRTUAL_CURRENCY_PACKS">
        <loading-sku-packs v-if="loadingTokenPacks" purchase-page />
        <template v-else>
          <div
            v-for="(pack, index) in webTokenPacks"
            :key="pack.id"
            class="tw-flex tw-justify-between tw-items-center game-tile-background-color tw-p-6 game-tile-radius"
          >
            <p class="tw-text-h5 tw-font-medium">
              {{ pack.amount.toLocaleString() }}
              {{ game.token_name || tokenName().value }}
            </p>
            <base-button
              :test-id="`token-pack-${index}`"
              primary
              @click="selectTokenPack(pack)"
            >
              {{ humanPriceFormat(convertToDecimal(pack.price)) }}
            </base-button>
          </div>
        </template>
      </div>

      <p class="tw-text-center">
        <i18n-t keypath="marketplace.vc_agreement">
          <template #terms>
            <a
              class="mod-link"
              href="https://mod.io/terms"
              @click.prevent="openTermsModal()"
              >{{ $t('terms') }}
            </a>
          </template>
          <template #refund>
            <a
              class="mod-link"
              href="https://mod.io/refund"
              @click.prevent="openRefundPolicyModal()"
              >{{ $t('refund') }}</a
            >
          </template>
        </i18n-t>
      </p>
    </div>
  </div>

  <teleport to="body">
    <token-purchase-modal
      v-if="isTokenModalOpen"
      :game="game"
      :mod="mod"
      :selected-pack="selectedPack"
      @close:purchasemodal="hideTokenModal"
    />
  </teleport>
</template>

<script>
import TokenPurchaseModal from '@components/Modals/TokenPurchaseModal.vue'
import { monetizationStore, tokenPackStore, authStore } from '@stores'
import { MODAL_AGREEMENT, MODAL_LOGIN } from '@config/globalModals.js'
import LoadingSkuPacks from '@components/Loading/LoadingSkuPacks.vue'
import BrowseTabs from '@components/Navigation/BrowseTabs.vue'
import { onMounted, computed, toRefs, watch, ref } from 'vue'
import { setGlobalModal } from '@composables/useModal.js'
import { getGameTokenPacksRequest } from '@services'
import Classes from '@shared/js/config/css.js'
import { clone } from '@helpers/utils.js'
import { useI18n } from 'vue-i18n'
import {
  useResourceValue,
  useMonetization,
  useAsync,
  useModal,
  useRoute,
} from '@composables'
import {
  AGREEMENT_TYPE_REFUND,
  AGREEMENT_TYPE_TOU,
  BROWSE_TABS,
} from '@config/options.js'
import {
  humanPriceFormat,
  convertToDecimal,
} from '@helpers/monetizationUtils.js'

export default {
  components: {
    TokenPurchaseModal,
    LoadingSkuPacks,
    BrowseTabs,
  },
  props: {
    game: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { game } = toRefs(props)
    const selectedPack = ref(null)
    const mod = ref(null)

    const { tokenPacks, setTokenPacks } = tokenPackStore(game.value.name_id)
    const { tiliaCallback, setTiliaCallback } = monetizationStore()
    const { isLoggedIn } = authStore()

    const { purchaseMod } = useMonetization()
    const { tokenName } = useResourceValue()
    const { routeQuery } = useRoute()
    const { t } = useI18n()
    const {
      isModalOpen: isTokenModalOpen,
      showModal: showTokenModal,
      hideModal: hideTokenModal,
    } = useModal()

    const {
      loading: loadingTokenPacks,
      error: errorTokenPacks,
      data: dataTokenPacks,
      run: runTokenPacks,
    } = useAsync(
      () => getGameTokenPacksRequest(game.value.name_id),
      t('marketplace.error_virtual_currency_packs')
    )

    const webTokenPacks = computed(() =>
      tokenPacks.value?.filter((p) => p.portal === 'web')
    )

    function checkCallback() {
      if (
        routeQuery.value['tilia-state'] &&
        routeQuery.value['tilia-state'].includes('paypal') &&
        tiliaCallback.value?.sku
      ) {
        const pack = webTokenPacks.value.find(
          (t) => t.id === tiliaCallback.value.sku
        )
        if (pack) {
          selectTokenPack(pack)
          setTiliaCallback('sku', null)
        }
        history.replaceState({}, '', `${location.origin}${location.pathname}`)
      } else if (routeQuery.value.purchase) {
        const pack = webTokenPacks.value.find(
          (t) =>
            t.amount === Number(routeQuery.value.purchase) && t.portal === 'web'
        )
        if (pack) {
          selectTokenPack(pack)
        }
      }
    }

    onMounted(async () => {
      mod.value = clone(purchaseMod.value)
      purchaseMod.value = null

      await runTokenPacks()

      if (!errorTokenPacks.value) {
        setTokenPacks(dataTokenPacks.value.data)
        checkCallback()
      }
    })

    watch(isLoggedIn, checkCallback)

    async function selectTokenPack(pack) {
      if (!isLoggedIn.value) {
        setGlobalModal({ modal: MODAL_LOGIN })
        return
      }

      selectedPack.value = pack
      setTiliaCallback('sku', pack.id)
      showTokenModal()
    }

    function openTermsModal() {
      setGlobalModal({
        modal: MODAL_AGREEMENT,
        data: { agreementType: AGREEMENT_TYPE_TOU },
      })
    }

    function openRefundPolicyModal() {
      setGlobalModal({
        modal: MODAL_AGREEMENT,
        data: { agreementType: AGREEMENT_TYPE_REFUND },
      })
    }

    return {
      openRefundPolicyModal,
      loadingTokenPacks,
      humanPriceFormat,
      convertToDecimal,
      isTokenModalOpen,
      selectTokenPack,
      hideTokenModal,
      openTermsModal,
      webTokenPacks,
      selectedPack,
      BROWSE_TABS,
      tokenName,
      Classes,
      mod,
    }
  },
}
</script>
